import {
  Children,
  forwardRef,
  cloneElement,
  createElement,
  type ReactElement,
  type ComponentType,
  type ReactNode,
  type ComponentPropsWithoutRef,
  type Ref,
} from "react";
import classnames from "classnames";

import { useId } from "util/html";

import { FormattedFieldError, isAriaInvalid, type AriaInvalidPropValue } from "../error";
import type { FieldPath, UseFormReturn, FieldValues, FieldError } from "..";
import Styles from "./index.module.scss";
import RequiredAsterisk from "../required-asterisk";

type RowProps = ComponentPropsWithoutRef<"div">;
type FormRowProps = RowProps & { fullWidth?: boolean };
type RowElementProps = { id?: string; "aria-invalid": AriaInvalidPropValue };
type AutoRowProps<FormValues extends FieldValues> = {
  "aria-label"?: string;
  fullWidth?: boolean;
  placeholder?: string;
  className?: string;
  form: UseFormReturn<FormValues>;
  name: FieldPath<FormValues>;
  registerOptions?: Parameters<UseFormReturn<FormValues>["register"]>[1];
  helperText?: ReactNode;
  dynamicHeightError?: boolean;
  highlightLabelOnError?: boolean;
} & (
  | {
      children: ReactElement<RowElementProps>;
      as?: never;
    }
  | {
      children?: never;
      as: ComponentType<RowElementProps>;
    }
) &
  (
    | {
        label: ReactNode;
        required?: boolean;
      }
    | {
        label?: never;
        required?: never;
      }
  );

function Label(props: ComponentPropsWithoutRef<"label">, ref: Ref<HTMLLabelElement>) {
  return <label {...props} className={classnames(Styles.label, props.className)} ref={ref} />;
}
const LabelWithRef = forwardRef(Label);

export function FormRow({ fullWidth, ...props }: FormRowProps) {
  return (
    <div
      {...props}
      className={classnames(Styles.row, props.className, {
        [Styles.fullWidth]: fullWidth,
      })}
    />
  );
}

export function MultipartFormRow(props: RowProps) {
  return <div {...props} className={classnames(Styles.multiRow, props.className)} />;
}

export function SpacedMultipartFormRow(props: RowProps) {
  return (
    <div {...props} className={classnames(Styles.multiRow, props.className)}>
      {Children.map(props.children, (child: ReactNode, index) => (
        <div key={index} className={Styles.spacedInput}>
          {child}
        </div>
      ))}
    </div>
  );
}

export function AutomaticFormRow<FormValues extends FieldValues>(props: AutoRowProps<FormValues>) {
  const {
    form,
    name,
    label,
    registerOptions,
    fullWidth,
    children,
    "aria-label": ariaLabel,
    placeholder,
    className,
    helperText,
    required,
  } = props;
  const error = form.formState.errors[name] as undefined | FieldError;
  const originalId = props.children?.props.id;
  const uniqueId = useId();
  const realId = originalId || uniqueId;
  const autoElementProps = {
    ...form.register(name, registerOptions),
    id: realId,
    "aria-invalid": isAriaInvalid(error),
    "aria-label": ariaLabel,
    placeholder,
  };
  const element = children
    ? cloneElement(children, autoElementProps)
    : createElement(props.as, autoElementProps);
  return (
    <FormRow fullWidth={fullWidth} className={className}>
      {label && (
        <LabelWithRef htmlFor={realId}>
          {label}
          {required && <RequiredAsterisk />}
        </LabelWithRef>
      )}
      {helperText && <p className={Styles.helperText}>{helperText}</p>}
      {element}
      <FormattedFieldError inputName={name} error={error} />
    </FormRow>
  );
}

export { LabelWithRef as Label };
